import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { getBackendUrl } from '../utils/config';

const AuthContext = createContext(null);

const getApiUrl = () => `${getBackendUrl()}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('horeca_token'));
  const [loading, setLoading] = useState(true);

  const api = axios.create({
    baseURL: getApiUrl(),
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  });

  api.interceptors.request.use((config) => {
    const currentToken = localStorage.getItem('horeca_token');
    if (currentToken) {
      config.headers.Authorization = `Bearer ${currentToken}`;
    }
    return config;
  });

  const fetchUser = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const response = await api.get('/auth/me');
      const userData = response.data;
      setUser(userData);
      
      // Set subscription - admins always have access
      if (userData.role === 'admin') {
        setSubscription({ status: 'admin', has_access: true });
      } else if (userData.subscription) {
        setSubscription(userData.subscription);
      } else {
        setSubscription({ status: 'none', has_access: false });
      }
    } catch (error) {
      console.error('Auth error:', error);
      localStorage.removeItem('horeca_token');
      setToken(null);
      setUser(null);
      setSubscription(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const refreshSubscription = async () => {
    try {
      const response = await api.get('/subscription/status');
      setSubscription(response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching subscription:', error);
      return null;
    }
  };

  const login = async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    const { token: newToken, user: userData, subscription: subData } = response.data;
    localStorage.setItem('horeca_token', newToken);
    setToken(newToken);
    setUser(userData);
    setSubscription(subData);
    return { user: userData, subscription: subData };
  };

  const register = async (email, password, name, role = 'company') => {
    const response = await api.post('/auth/register', { email, password, name, role });
    // Registration now requires email activation - don't auto-login
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem('horeca_token');
    setToken(null);
    setUser(null);
    setSubscription(null);
  };

  const hasPermission = (module) => {
    if (!user) return false;
    if (user.role === 'company' || user.role === 'admin') return true;
    return user.permissions?.[module] === true;
  };

  // Check if user has valid subscription access
  const hasSubscriptionAccess = () => {
    if (!user) return false;
    // Admins always have access
    if (user.role === 'admin') return true;
    // Check subscription status
    return subscription?.has_access === true;
  };

  // Check if user is in trial period
  const isInTrial = () => {
    return subscription?.status === 'trial';
  };

  // Get trial days remaining
  const getTrialDaysRemaining = () => {
    if (!subscription?.trial_end) return 0;
    const trialEnd = new Date(subscription.trial_end);
    const now = new Date();
    const diffTime = trialEnd - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  };

  return (
    <AuthContext.Provider value={{
      user,
      token,
      loading,
      subscription,
      login,
      register,
      logout,
      hasPermission,
      hasSubscriptionAccess,
      isInTrial,
      getTrialDaysRemaining,
      refreshSubscription,
      api
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
